import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import grapesjs, { type Editor, type Plugin } from 'grapesjs'
import mjmlPlugin from 'grapesjs-mjml'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { NewsletterStatusPill } from '@/components/ui/NewsletterStatusPill'
import {
  createCampaign,
  getCampaign,
  renderMjmlToHtml,
  STARTER_MJML,
  updateCampaign,
} from '@/lib/newsletter'
import type { NewsletterAudience, NewsletterCampaign } from '@/lib/newsletter-types'

type SaveMode = 'draft' | 'created'

export function NewsletterBuilderPage() {
  const { newsletterId } = useParams<{ newsletterId: string }>()
  const isEditing = !!newsletterId
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const editorContainerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<Editor | null>(null)
  // Mirrors `initialMjml` for the GrapeJS `load` handler so it always
  // reads the freshest seeded MJML. Without this ref, the handler's
  // closure captures the value from the render in which the editor was
  // mounted — `STARTER_MJML` on first render — which is then committed to
  // the canvas even after the campaign's MJML arrives from cache or API.
  const initialMjmlRef = useRef<string>(STARTER_MJML)

  const [subject, setSubject] = useState('Subject line here')
  const [audience, setAudience] = useState<NewsletterAudience>('free')
  const [saveMode, setSaveMode] = useState<SaveMode>('draft')
  const [renderedHtml, setRenderedHtml] = useState<string>('')
  const [initialMjml, setInitialMjml] = useState<string>(STARTER_MJML)
  const [renderError, setRenderError] = useState<string | null>(null)

  const existingCampaignQ = useQuery({
    queryKey: ['admin-newsletter-campaign', newsletterId],
    queryFn: () => getCampaign(newsletterId!),
    enabled: isEditing,
  })

  useEffect(() => {
    if (!existingCampaignQ.data) return
    const c: NewsletterCampaign = existingCampaignQ.data
    if (c.status !== 'draft') {
      // Editing a non-draft is rejected server-side; surface that here too.
      setSaveMode('created')
    } else {
      setSaveMode('draft')
    }
    if (c.subject) setSubject(c.subject)
    if (c.audience) setAudience(c.audience)
    if (c.mjml) setInitialMjml(c.mjml)
    if (c.html) setRenderedHtml(c.html)
  }, [existingCampaignQ.data])

  // Keep the ref in lockstep with the seeded MJML so the canvas-load
  // handler below resolves to the latest value, not the value that was
  // current when the editor was first mounted.
  useEffect(() => {
    initialMjmlRef.current = initialMjml
  }, [initialMjml])

  // Initialize GrapeJS once. The container div must be in the DOM.
  useEffect(() => {
    if (!editorContainerRef.current) return
    if (editorRef.current) return

    const editor = grapesjs.init({
      container: editorContainerRef.current,
      fromElement: false,
      height: '640px',
      width: 'auto',
      storageManager: false,
      plugins: [mjmlPlugin as unknown as Plugin],
      pluginsOpts: {
        [mjmlPlugin as unknown as string]: {
          // Keep the editor focused on the MJML preset only.
          resetDevices: true,
          hideSelector: true,
          useCustomTheme: true,
        },
      },
      canvas: {
        styles: [
          'https://fonts.googleapis.com/css?family=Montserrat:400,600,700&display=swap',
        ],
      },
    })

    // Seed the canvas with the latest seeded MJML once the editor is
    // loaded. We read the ref instead of the closed-over `initialMjml`
    // so a campaign arriving from React Query cache before the canvas
    // mounts is rendered, not the `STARTER_MJML` placeholder.
    editor.on('load', () => {
      try {
        editor.setComponents(initialMjmlRef.current)
        editor.setStyle('')
      } catch (e) {
        console.error('[Newsletter] failed to seed components', e)
      }
    })

    editorRef.current = editor

    return () => {
      try {
        editor.destroy()
      } catch (e) {
        console.warn('[Newsletter] editor.destroy() threw', e)
      }
      editorRef.current = null
    }
    // We intentionally only init once. Subsequent MJML loads come from the editor's own undo/redo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep the editor's MJML in sync if the data load supplies new initial content.
  useEffect(() => {
    const editor = editorRef.current
    if (!editor || !isEditing || !existingCampaignQ.data) return
    try {
      editor.setComponents(initialMjml)
      editor.setStyle('')
    } catch (e) {
      console.warn('[Newsletter] failed to push seeded MJML', e)
    }
  }, [initialMjml, isEditing, existingCampaignQ.data])

  // Pull current MJML + HTML out of the editor.
  // We use grapesjs-mjml's built-in `mjml-code` command rather than calling
  // `editor.getComponents().serialize(...)` — the latter returns the
  // grapesjs component model JSON which is not valid MJML markup.
  const collectMjmlAndHtml = async (): Promise<{ mjml: string; html: string; error?: string }> => {
    const editor = editorRef.current
    if (!editor) return { mjml: '', html: '', error: 'editor_not_ready' }
    try {
      const result = editor.runCommand('mjml-code') as unknown as string | Promise<string>
      const mjml = typeof result === 'string' ? result : await result
      if (!mjml || !mjml.trim()) return { mjml: '', html: '', error: 'empty_mjml' }
      const html = await renderMjmlToHtml(mjml)
      return { mjml, html }
    } catch (e) {
      return { mjml: '', html: '', error: String(e) }
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { mjml, html, error } = await collectMjmlAndHtml()
      if (error) throw new Error(error)
      if (!subject.trim()) throw new Error('subject_required')
      if (!mjml.trim()) throw new Error('mjml_required')
      if (!html.trim()) throw new Error('html_required')
      setRenderedHtml(html)
      setRenderError(null)

      if (isEditing && newsletterId) {
        const updated = await updateCampaign(newsletterId, {
          subject: subject.trim(),
          mjml,
          html,
          audience,
        })
        return updated
      }
      const created = await createCampaign({
        subject: subject.trim(),
        mjml,
        html,
        audience,
      })
      return created
    },
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaigns'] })
      if (!isEditing) {
        setSaveMode('created')
        navigate(`/newsletters/${campaign.id}/edit`, { replace: true })
      } else {
        queryClient.invalidateQueries({ queryKey: ['admin-newsletter-campaign', campaign.id] })
      }
    },
  })

  const audienceOptions = useMemo(
    () =>
      [
        { value: 'free', label: 'Free list', hint: 'newsletter_subscribers where status=active' },
        { value: 'paid', label: 'Paid list', hint: 'Trial + paid users (definition will tighten later)' },
        { value: 'both', label: 'Free + Paid', hint: 'Union, deduped' },
      ] as const,
    [],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <Link to="/newsletters" className="text-sm text-[var(--content-tertiary)] hover:text-[var(--content)]">
              ← Newsletters
            </Link>
            {isEditing && existingCampaignQ.data && (
              <NewsletterStatusPill status={existingCampaignQ.data.status} />
            )}
          </div>
          <h1 className="text-2xl font-heading font-semibold">
            {isEditing ? 'Edit campaign' : 'New campaign'}
          </h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="block min-w-[280px]">
            <span className="text-xs uppercase tracking-wider text-[var(--content-tertiary)]">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 block w-full rounded-md border border-[var(--border)] bg-[var(--surface-nested)] px-3 py-2"
            />
          </label>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : isEditing ? 'Save draft' : 'Save & continue'}
            </Button>
            {saveMode === 'created' && isEditing && newsletterId && (
              <Link to={`/newsletters/${newsletterId}`}>
                <Button>Open details</Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {saveMutation.error && (
        <p className="text-red-600 text-sm">{String(saveMutation.error)}</p>
      )}

      <Card>
        <CardHeader>
          <h2 className="font-heading font-semibold text-sm">Audience</h2>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {audienceOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setAudience(opt.value)}
                className={`flex-1 min-w-[200px] text-left rounded-lg border px-3 py-2 transition-colors ${
                  audience === opt.value
                    ? 'border-[var(--brand)] bg-[var(--brand)]/10'
                    : 'border-[var(--border)] hover:bg-[var(--surface-nested)]'
                }`}
              >
                <p className="font-medium text-sm">{opt.label}</p>
                <p className="text-xs text-[var(--content-tertiary)] mt-0.5">{opt.hint}</p>
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-[var(--content-tertiary)]">
            The "paid" definition is intentionally soft and will tighten once final billing rules settle.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-heading font-semibold text-sm">Design</h2>
        </CardHeader>
        <CardContent className="p-0">
          {existingCampaignQ.isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
          ) : (
            <div
              ref={editorContainerRef}
              className="border-t border-[var(--border)] min-h-[640px]"
            />
          )}
        </CardContent>
      </Card>

      {renderedHtml && (
        <Card>
          <CardHeader>
            <h2 className="font-heading font-semibold text-sm">Preview</h2>
          </CardHeader>
          <CardContent className="p-0">
            <iframe
              title="Live preview"
              srcDoc={renderedHtml}
              // Sandbox the preview: this HTML is freshly rendered from
              // MJML the admin just authored, but we still strip scripts and
              // same-origin access so a bug in MJML rendering cannot run JS
              // against the admin origin.
              sandbox=""
              className="w-full min-h-[420px] bg-white"
            />
          </CardContent>
        </Card>
      )}

      {renderError && (
        <p className="text-red-600 text-sm">{renderError}</p>
      )}
    </div>
  )
}
